import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Calendar, ChevronLeft, ChevronRight, Download, Eye, Funnel, LUCIDE_ICONS, LucideAngularModule, LucideIconProvider, Pencil, Search, Trash2, X } from 'lucide-angular';
import { FiltroActivoKey } from '../../../../core/models';
import { RolPlataforma } from '../../../../core/models/novedades.models';
import { Postulacion } from '../../../../core/models/postulaciones.models';
import { PostulacionesLogService } from '../../../../core/services/postulaciones-log.service';
import { PatrocinioDecisionesService } from '../../../../core/services/patrocinio-decisiones.service';
import { PostulacionesService } from '../../../../core/services/postulaciones.service';
import {
  avanceVisiblePostulacion,
  esEstadoVisibleMisPostulaciones,
  esPostulacionEnviada,
  etiquetaEstadoPostulacion,
  filtrarYOrdenarPostulaciones,
  folioVisiblePostulacion,
  obtenerIdProyectoPostulacion,
  PostulacionesFiltroCriteria,
} from '../../../../core/utils/postulaciones-filter.util';
import {
  BUSQUEDA_MINIMA_INSTITUCIONAL,
  construirCatalogosPostulaciones,
  construirCriteriaFiltros,
  construirFiltrosActivos,
  FiltroActivoItem,
  obtenerPostulacionesVisiblesPorRol,
  OrdenCampo,
  OrdenDireccion,
  PerfilPostulacionesFiltrosState,
  valorPorDefectoFiltro as obtenerValorPorDefectoFiltro,
} from './perfil-postulaciones.helpers';
import { construirCsvPostulaciones, construirNombreDescargaPostulaciones } from './perfil-postulaciones.export';
import {
  ModoAccesoPostulacion,
  mostrarSoloVisualizacionInstitucional,
  obtenerDetalleEstadoRepresentante,
  puedeEditarPostulacionComoAyudante,
  puedeEliminarPostulacion,
  resolverModoAccesoPorRol,
} from './perfil-postulaciones.roles';
import { EmptyFilterState } from '../../../../shared/components/empty-filter-state/empty-filter-state';
import { TablePagination } from '../../../../shared/components/table-pagination/table-pagination';

type AccionRegistro = 'acceder_formulario' | 'editar_formulario' | 'solo_lectura' | 'eliminar';

@Component({
  selector: 'app-perfil-postulaciones-tab',
  imports: [CommonModule, FormsModule, LucideAngularModule, EmptyFilterState, TablePagination],
  providers: [
    {
      provide: LUCIDE_ICONS,
      multi: true,
      useValue: new LucideIconProvider({
        Calendar,
        ChevronLeft,
        ChevronRight,
        Download,
        Eye,
        Funnel,
        Pencil,
        Search,
        Trash2,
        X,
      }),
    },
  ],
  templateUrl: './perfil-postulaciones-tab.html',
  styleUrl: './perfil-postulaciones-tab.css',
})
export class PerfilPostulacionesTab implements OnInit, OnChanges {
  @Input({ required: true }) rolSeleccionado: RolPlataforma = 'postulante';

  todasPostulaciones: Postulacion[] = [];
  todasPostulacionesOriginales: Postulacion[] = [];
  postulaciones: Postulacion[] = [];
  postulacionAEliminar: Postulacion | null = null;
  modalEliminarVisible = false;

  mostrarFiltrosAvanzados = true;
  mensajeBusquedaMinima = '';
  mensajeAccion = '';
  busquedaInstitucionalEjecutada = false;
  subdirecciones: string[] = [];
  estadosPostulacion: string[] = [];
  anios: number[] = [];
  idsPostulacion: string[] = [];
  idsProyectoDisponibles: string[] = [];
  foliosDisponibles: string[] = [];
  proyectosDisponibles: string[] = [];
  departamentosDisponibles: string[] = [];
  concursos: string[] = [];

  concursoSeleccionado = 'Todos';
  subdireccionSeleccionada = 'Todas';
  estadoSeleccionado = 'Todos';
  anioSeleccionado = 'Todos';
  idPostulacionBusqueda = '';
  idProyectoBusqueda = 'Todos';
  folioBusqueda = 'Todos';
  proyectoBusqueda = '';
  subdireccionBusqueda = 'Todas';
  estadoBusqueda = 'Todos';
  departamentoBusqueda = 'Todos';
  busquedaGeneral = '';
  ordenarPor: OrdenCampo = 'fecha_termino';
  ordenSeleccionado: OrdenDireccion = 'asc';
  readonly opcionesCantidad = [10, 20, 30, 40, 50];
  cantidadPorPagina = 10;
  paginaActual = 1;

  constructor(
    private readonly postulacionesService: PostulacionesService,
    private readonly postulacionesLogService: PostulacionesLogService,
    private readonly patrocinioDecisionesService: PatrocinioDecisionesService,
    private readonly router: Router
  ) {}

  // ---------------------------
  // Ciclo de vida
  // ---------------------------
  ngOnInit(): void {
    // Carga base de postulaciones y aplica filtros iniciales por rol.
    this.postulacionesService.getMisPostulaciones().subscribe((data) => {
      this.todasPostulacionesOriginales = data;
      this.actualizarPostulacionesSegunRol();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Si el rol cambia desde el contenedor, recalcula dataset y filtros.
    if (changes['rolSeleccionado'] && !changes['rolSeleccionado'].firstChange) {
      this.actualizarPostulacionesSegunRol();
    }
  }

  // ---------------------------
  // Estado de rol y vista
  // ---------------------------
  get esRolPostulante(): boolean {
    return this.rolSeleccionado === 'postulante';
  }

  get esRolAyudantePostulante(): boolean {
    return this.rolSeleccionado === 'ayudante_postulante';
  }

  get esRolRepresentante(): boolean {
    return this.rolSeleccionado === 'representante_institucional';
  }

  get esRolApoyoRepresentante(): boolean {
    return this.rolSeleccionado === 'ayudante_representante';
  }

  get esRolInstitucional(): boolean {
    return this.esRolRepresentante || this.esRolApoyoRepresentante;
  }

  get esRolPostulanteOAyudante(): boolean {
    return this.esRolPostulante || this.esRolAyudantePostulante;
  }

  get filtrosActivos(): FiltroActivoItem[] {
    return construirFiltrosActivos(this.obtenerEstadoFiltros());
  }

  // ---------------------------
  // Filtros y orden
  // ---------------------------
  aplicarFiltros(): void {
    // El filtrado y ordenamiento esta centralizado en util puro para mantener consistencia.
    this.mensajeBusquedaMinima = '';

    if (this.esRolInstitucional && this.hasTexto(this.busquedaGeneral) && this.busquedaGeneral.trim().length < BUSQUEDA_MINIMA_INSTITUCIONAL) {
      this.mensajeBusquedaMinima = 'La busqueda requiere minimo 4 caracteres.';
    }

    this.postulaciones = filtrarYOrdenarPostulaciones(this.todasPostulaciones, this.obtenerCriteriaFiltros());
    this.paginaActual = 1;
  }

  ejecutarBusquedaInstitucional(): void {
    this.busquedaInstitucionalEjecutada = true;
    this.aplicarFiltros();
  }

  limpiarFiltro(key: FiltroActivoKey): void {
    this.setFiltroPorDefecto(key);
    this.aplicarFiltros();
  }

  toggleFiltrosAvanzados(event: Event): void {
    event.preventDefault();
    this.mostrarFiltrosAvanzados = !this.mostrarFiltrosAvanzados;
  }

  limpiarTodosFiltrosAvanzados(): void {
    this.idPostulacionBusqueda = 'Todos';
    this.idProyectoBusqueda = 'Todos';
    this.folioBusqueda = 'Todos';
    this.proyectoBusqueda = 'Todos';
    this.subdireccionBusqueda = 'Todas';
    this.estadoBusqueda = 'Todos';
    this.departamentoBusqueda = 'Todos';
    this.busquedaGeneral = '';
    this.busquedaInstitucionalEjecutada = false;
    this.aplicarFiltros();
  }

  limpiarBusquedaYFiltrosInstitucionales(): void {
    this.concursoSeleccionado = 'Todos';
    this.subdireccionSeleccionada = 'Todas';
    this.estadoSeleccionado = 'Todos';
    this.anioSeleccionado = 'Todos';
    this.idPostulacionBusqueda = 'Todos';
    this.idProyectoBusqueda = 'Todos';
    this.folioBusqueda = 'Todos';
    this.proyectoBusqueda = 'Todos';
    this.subdireccionBusqueda = 'Todas';
    this.estadoBusqueda = 'Todos';
    this.departamentoBusqueda = 'Todos';
    this.busquedaGeneral = '';
    this.busquedaInstitucionalEjecutada = false;
    this.aplicarFiltros();
  }

  get mostrarSinResultadosInstitucional(): boolean {
    if (!this.esRolInstitucional) return false;
    if (!this.busquedaInstitucionalEjecutada) return false;
    if (this.busquedaGeneral.trim().length < BUSQUEDA_MINIMA_INSTITUCIONAL) return false;
    return this.postulaciones.length === 0;
  }

  get postulacionesPaginadas(): Postulacion[] {
    const paginaTotal = this.totalPaginas;
    if (this.paginaActual > paginaTotal) {
      this.paginaActual = paginaTotal;
    }

    const inicio = (this.paginaActual - 1) * this.cantidadPorPagina;
    return this.postulaciones.slice(inicio, inicio + this.cantidadPorPagina);
  }

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.postulaciones.length / this.cantidadPorPagina));
  }

  cambiarPagina(pagina: number): void {
    this.paginaActual = pagina;
  }

  cambiarCantidadDesdePaginador(cantidad: number): void {
    this.cantidadPorPagina = cantidad;
    this.paginaActual = 1;
  }

  // ---------------------------
  // Reglas por rol
  // ---------------------------
  get paginasVisibles(): number[] {
    const total = this.totalPaginas;
    return Array.from({ length: total }, (_, index) => index + 1);
  }

  puedeEliminar(item: Postulacion): boolean {
    return puedeEliminarPostulacion(this.rolSeleccionado, item);
  }

  puedeEditarAyudante(item: Postulacion): boolean {
    return puedeEditarPostulacionComoAyudante(this.rolSeleccionado, item);
  }

  sinAcciones(item: Postulacion): boolean {
    return mostrarSoloVisualizacionInstitucional(this.rolSeleccionado, item);
  }

  mostrarLapiz(item: Postulacion): boolean {
    return this.resolverModoAcceso(item) === 'editar';
  }

  mostrarOjo(item: Postulacion): boolean {
    if (this.sinAcciones(item)) return false;
    return !this.mostrarLapiz(item);
  }

  mostrarDescarga(item: Postulacion): boolean {
    return esPostulacionEnviada(item.estadoPostulacion);
  }

  lineaDePostulacion(item: Postulacion): string {
    return item.subdireccion || '—';
  }

  folioDePostulacion(item: Postulacion): string {
    return folioVisiblePostulacion(item);
  }

  fechaCierreDia(item: Postulacion): string {
    const partes = this.partirFechaCierre(item.fechaTermino);
    return partes.fecha;
  }

  fechaCierreHora(item: Postulacion): string {
    return this.partirFechaCierre(item.fechaTermino).hora;
  }

  avanceDePostulacion(item: Postulacion): string {
    return `${avanceVisiblePostulacion(item)}%`;
  }

  etiquetaAccionPostulante(): string {
    return 'Acceder a postulación';
  }

  mostrarFolio(item: Postulacion): boolean {
    return esPostulacionEnviada(item.estadoPostulacion) && !!item.folio && item.folio !== '-';
  }

  obtenerIdProyecto(item: Postulacion): string {
    return obtenerIdProyectoPostulacion(item);
  }

  estadoCompleto(item: Postulacion): string {
    return etiquetaEstadoPostulacion(item.estadoPostulacion);
  }

  detalleEstadoRepresentante(item: Postulacion): string {
    return obtenerDetalleEstadoRepresentante(this.rolSeleccionado, item);
  }

  claseEstadoPostulacion(estado: Postulacion['estadoPostulacion']): string {
    if (estado === 'en_preparacion') return 'badge-preparacion';
    if (estado === 'adjudicado') return 'badge-success';
    if (estado === 'enviada') return 'badge-enviada';
    if (estado === 'observada') return 'badge-warning';
    if (estado === 'proximo') return 'badge-info';
    if (estado === 'en_revision') return 'badge-revision';
    return 'badge-primary';
  }

  // ---------------------------
  // Acciones de usuario
  // ---------------------------
  abrirModalEliminar(item: Postulacion): void {
    // Solo habilitado para postulante en estado en preparacion.
    if (!this.puedeEliminar(item)) return;
    this.postulacionAEliminar = item;
    this.modalEliminarVisible = true;
  }

  cerrarModalEliminar(): void {
    this.modalEliminarVisible = false;
    this.postulacionAEliminar = null;
  }

  confirmarEliminar(): void {
    // Eliminacion local (mock): al integrar backend se reemplaza por endpoint real.
    if (!this.postulacionAEliminar) return;

    this.registrarLog(this.postulacionAEliminar, 'eliminar', 'Postulación eliminada desde lista.');
    this.todasPostulaciones = this.todasPostulaciones.filter((p) => p.idPostulacion !== this.postulacionAEliminar!.idPostulacion);
    this.aplicarFiltros();
    this.cerrarModalEliminar();
  }

  accederFormulario(item: Postulacion): void {
    const modo = this.resolverModoAcceso(item);

    if (modo === 'editar') {
      this.registrarLog(item, 'editar_formulario', 'Accede al formulario en modo edicion.');
      this.mensajeAccion = `Formulario abierto en modo edicion (${item.idPostulacion}).`;
    } else if (modo === 'solo_lectura' || modo === 'observar') {
      this.registrarLog(item, 'solo_lectura', 'Accede al formulario en modo lectura.');
      this.mensajeAccion = `Formulario abierto en modo solo lectura (${item.idPostulacion}).`;
    } else {
      this.registrarLog(item, 'acceder_formulario', 'Accede al formulario de la postulacion.');
      this.mensajeAccion = `Formulario abierto (${item.idPostulacion}).`;
    }

    this.router.navigate(['/postulaciones/formulario', item.idPostulacion], {
      queryParams: { role: this.rolSeleccionado },
    });
  }

  descargarPostulacion(item: Postulacion): void {
    const csv = construirCsvPostulaciones(
      [item],
      this.esRolInstitucional,
      (registro) => this.obtenerIdProyecto(registro),
      (registro) => this.estadoCompleto(registro)
    );
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `postulacion-${item.idPostulacion}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    this.mensajeAccion = `Descarga generada (${item.idPostulacion}). Pendiente de documento oficial de API.`;
    this.registrarLog(item, 'acceder_formulario', 'Descarga local del registro; documento oficial pendiente de API.');
  }

  descargarPostulacionesFiltradas(): void {
    const csv = construirCsvPostulaciones(
      this.postulaciones,
      this.esRolInstitucional,
      (item) => this.obtenerIdProyecto(item),
      (item) => this.estadoCompleto(item)
    );
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const fecha = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download = construirNombreDescargaPostulaciones(fecha);
    link.click();

    URL.revokeObjectURL(url);

    this.mensajeAccion = `Descarga generada (${this.postulaciones.length} registros).`;
    this.postulacionesLogService.registrar({
      timestamp: new Date().toISOString(),
      rol: this.rolSeleccionado,
      idPostulacion: '-',
      folio: '-',
      accion: 'descargar',
      detalle: `Descarga CSV con ${this.postulaciones.length} registros filtrados.`,
    });
  }

  // ---------------------------
  // Helpers privados
  // ---------------------------
  private actualizarPostulacionesSegunRol(): void {
    const conDecisiones = this.patrocinioDecisionesService.aplicarDecisiones(this.todasPostulacionesOriginales);
    this.todasPostulaciones = obtenerPostulacionesVisiblesPorRol(
      conDecisiones,
      this.rolSeleccionado,
      this.esRolRepresentante
    ).filter((item) => esEstadoVisibleMisPostulaciones(item.estadoPostulacion));
    if (this.esRolInstitucional) {
      if (!this.idPostulacionBusqueda) this.idPostulacionBusqueda = 'Todos';
      if (!this.proyectoBusqueda) this.proyectoBusqueda = 'Todos';
    } else {
      if (this.idPostulacionBusqueda === 'Todos') this.idPostulacionBusqueda = '';
      if (this.proyectoBusqueda === 'Todos') this.proyectoBusqueda = '';
    }
    this.actualizarCatalogos();
    this.busquedaInstitucionalEjecutada = false;
    this.aplicarFiltros();
  }

  private obtenerCriteriaFiltros(): PostulacionesFiltroCriteria {
    return construirCriteriaFiltros(this.obtenerEstadoFiltros(), this.rolSeleccionado, this.esRolInstitucional);
  }

  private valorPorDefectoFiltro(key: FiltroActivoKey): string {
    return obtenerValorPorDefectoFiltro(key);
  }

  private setFiltroPorDefecto(key: FiltroActivoKey): void {
    const valor = this.valorPorDefectoFiltro(key);
    switch (key) {
      case 'concursoSeleccionado':
        this.concursoSeleccionado = valor;
        return;
      case 'subdireccionSeleccionada':
        this.subdireccionSeleccionada = valor;
        return;
      case 'estadoSeleccionado':
        this.estadoSeleccionado = valor;
        return;
      case 'anioSeleccionado':
        this.anioSeleccionado = valor;
        return;
      case 'idProyectoBusqueda':
        this.idProyectoBusqueda = valor;
        return;
      case 'idPostulacionBusqueda':
        this.idPostulacionBusqueda = valor;
        return;
      case 'folioBusqueda':
        this.folioBusqueda = valor;
        return;
      case 'proyectoBusqueda':
        this.proyectoBusqueda = valor;
        return;
      case 'subdireccionBusqueda':
        this.subdireccionBusqueda = valor;
        return;
      case 'estadoBusqueda':
        this.estadoBusqueda = valor;
        return;
      case 'departamentoBusqueda':
        this.departamentoBusqueda = valor;
        return;
      case 'busquedaGeneral':
        this.busquedaGeneral = valor;
        return;
    }
  }

  private hasTexto(value: string): boolean {
    return value.trim().length > 0;
  }

  private resolverModoAcceso(item: Postulacion): ModoAccesoPostulacion {
    return resolverModoAccesoPorRol(this.rolSeleccionado, item);
  }

  private actualizarCatalogos(): void {
    const catalogos = construirCatalogosPostulaciones(this.todasPostulaciones);
    this.idsPostulacion = catalogos.idsPostulacion;
    this.idsProyectoDisponibles = catalogos.idsProyectoDisponibles;
    this.foliosDisponibles = catalogos.foliosDisponibles;
    this.proyectosDisponibles = catalogos.proyectosDisponibles;
    this.departamentosDisponibles = catalogos.departamentosDisponibles;
    this.concursos = catalogos.concursos;
    this.subdirecciones = catalogos.subdirecciones;
    this.estadosPostulacion = catalogos.estadosPostulacion;
    this.anios = catalogos.anios;
  }

  private obtenerEstadoFiltros(): PerfilPostulacionesFiltrosState {
    return {
      concursoSeleccionado: this.concursoSeleccionado,
      subdireccionSeleccionada: this.subdireccionSeleccionada,
      estadoSeleccionado: this.estadoSeleccionado,
      anioSeleccionado: this.anioSeleccionado,
      idPostulacionBusqueda: this.idPostulacionBusqueda,
      idProyectoBusqueda: this.idProyectoBusqueda,
      folioBusqueda: this.folioBusqueda,
      proyectoBusqueda: this.proyectoBusqueda,
      subdireccionBusqueda: this.subdireccionBusqueda,
      estadoBusqueda: this.estadoBusqueda,
      departamentoBusqueda: this.departamentoBusqueda,
      busquedaGeneral: this.busquedaGeneral,
      ordenarPor: this.ordenarPor,
      ordenSeleccionado: this.ordenSeleccionado,
    };
  }

  private partirFechaCierre(valor: string): { fecha: string; hora: string } {
    const match = valor.trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
    if (!match) {
      return { fecha: valor || '—', hora: '' };
    }
    const fecha = `${match[3]}/${match[2]}/${match[1]}`;
    const hora = match[4] && match[5] ? `${match[4]}:${match[5]} h` : '';
    return { fecha, hora };
  }

  private registrarLog(item: Postulacion, accion: AccionRegistro, detalle: string): void {
    this.postulacionesLogService.registrar({
      timestamp: new Date().toISOString(),
      rol: this.rolSeleccionado,
      idPostulacion: item.idPostulacion,
      folio: item.folio,
      accion,
      detalle,
    });
  }
}
